import type { LearningPurpose } from "../learning/types";
import type { PortalLocale } from "./portalShell";

const INTAKE_EN = {
  setupTitle: "Set up your plan",
  stepLabel: (current: number, total: number) => `${current} / ${total}`,
  back: "Back",
  next: "Next",
  finish: "Create plan",
  purposeTitle: "What are you here for?",
  detailsTitle: "A few details",
  roadmapTitle: "How should we plan?",
  bookTitle: "What are you reading?",
  paceTitle: "How much time do you have?",
  reviewTitle: "Ready to go",
  addSubject: "Add another subject",
  examName: "Exam or goal",
  subjects: "Subjects or topics",
  daysBudget: "Days until your goal",
  dailyMinutes: "Minutes per day",
  bookName: "Book title",
  purposeExam: "Crack an exam",
  purposeExamDesc: "UPSC, SSC, NEET, JEE, CAT — anything you're preparing for.",
  purposeSubject: "Master a subject",
  purposeSubjectDesc: "Go deep on a subject and truly understand it.",
  purposeSkill: "Build a skill",
  purposeSkillDesc: "Coding, a language, design — a skill you want to grow.",
  purposeSchool: "Stay ahead in school",
  purposeSchoolDesc: "Revise alongside what you're learning right now.",
  purposeCasual: "Read for pleasure",
  purposeCasualDesc: "No plan, no pressure — just great books to enjoy.",
  purposeOther: "Something else",
  purposeOtherDesc: "Tell us what you're here to work on.",
  pickWhatFits: "Pick what fits best — you can add more later.",
  intakeError503:
    "Our study coach is busy right now. Your plan could not be saved — try again shortly.",
  intakeErrorOffline: "Couldn't reach the server. Check your connection and try again.",
  intakeErrorSaveFailed: "Something went wrong saving your plan. Please try again.",
  wantRoadmapTitle: "Want a roadmap?",
  wantRoadmapBlurb:
    "I can map every topic and subtopic into a day-by-day plan — or you can follow your own.",
  whichExam: "Which exam?",
  examPlaceholder: "e.g. UPSC Civil Services",
  nameYourPlan: "Name your plan",
  addChip: (label: string) => `Add ${label.toLowerCase()}`,
  removeChip: (value: string) => `Remove ${value}`,
  planNamePlaceholder: "Give your plan a name",
  titleAutoHint: "We picked one for you — tweak it if you like.",
  whichBooks: "Which books?",
  bookStepCasualHint:
    "Tell me a book you want to read, or let me suggest great ones in your genres.",
  bookStepDefaultHint:
    "Follow a specific book, or let me recommend the best material for your goal.",
  bookTitlePlaceholder: "e.g. Laxmikanth — Indian Polity",
  paceBlurb: "I'll size your roadmap to fit — leave blank if you're not sure yet.",
  daysToGoal: "Days to reach your goal",
  minutesPerDay: "Minutes you can study each day",
  daysPlaceholder: "e.g. 180",
  minutesPlaceholder: "e.g. 120",
  reviewGoal: "Goal",
  reviewExam: "Exam",
  reviewFocus: "Focus",
  reviewRoadmap: "Roadmap",
  reviewBooks: "Books",
  reviewBook: "Book",
  reviewTimeline: "Timeline",
  reviewDaily: "Daily",
  reviewDays: (n: string) => `${n} days`,
  reviewMinPerDay: (n: string) => `${n} min/day`,
  reviewReadyHint: "Looks great — ready when you are.",
} as const;

type IntakeTable = {
  [K in keyof typeof INTAKE_EN]: (typeof INTAKE_EN)[K] extends (...args: infer A) => infer R
    ? (...args: A) => R
    : string;
};

const INTAKE_HI: IntakeTable = {
  setupTitle: "अपनी योजना बनाएँ",
  stepLabel: (current: number, total: number) => `${current} / ${total}`,
  back: "पीछे",
  next: "आगे",
  finish: "योजना बनाएँ",
  purposeTitle: "आप यहाँ किस लिए हैं?",
  detailsTitle: "कुछ जानकारी",
  roadmapTitle: "योजना कैसे बनाएँ?",
  bookTitle: "क्या पढ़ रहे हैं?",
  paceTitle: "कितना समय है?",
  reviewTitle: "तैयार हैं",
  addSubject: "एक और विषय जोड़ें",
  examName: "परीक्षा या लक्ष्य",
  subjects: "विषय",
  daysBudget: "लक्ष्य तक दिन",
  dailyMinutes: "रोज़ कितने मिनट",
  bookName: "पुस्तक का नाम",
  purposeExam: "परीक्षा की तैयारी",
  purposeExamDesc: "UPSC, SSC, NEET, JEE, CAT — जो भी लक्ष्य हो।",
  purposeSubject: "विषय में महारत",
  purposeSubjectDesc: "किसी विषय को गहराई से समझें।",
  purposeSkill: "कौशल बनाएँ",
  purposeSkillDesc: "कोडिंग, भाषा, डिज़ाइन — जो भी सीखना चाहते हैं।",
  purposeSchool: "स्कूल में आगे रहें",
  purposeSchoolDesc: "जो अभी पढ़ रहे हैं, उसके साथ रिवाइज़ करें।",
  purposeCasual: "मन से पढ़ाई",
  purposeCasualDesc: "कोई दबाव नहीं — बस अच्छी किताबें।",
  purposeOther: "कुछ और",
  purposeOtherDesc: "बताएँ आप किस पर काम करना चाहते हैं।",
  pickWhatFits: "जो सबसे सही लगे चुनें — बाद में और जोड़ सकते हैं।",
  intakeError503: "कोच अभी व्यस्त है। योजना सेव नहीं हो सकी — थोड़ी देर बाद फिर कोशिश करें।",
  intakeErrorOffline: "सर्वर तक नहीं पहुँचे। कनेक्शन जाँचें और फिर कोशिश करें।",
  intakeErrorSaveFailed: "योजना सेव करते समय समस्या। फिर कोशिश करें।",
  wantRoadmapTitle: "रोडमैप चाहिए?",
  wantRoadmapBlurb: "मैं हर विषय को दिन-दर-दिन योजना में रख सकता हूँ — या आप अपनी योजना अपनाएँ।",
  whichExam: "कौन सी परीक्षा?",
  examPlaceholder: "उदा. UPSC Civil Services",
  nameYourPlan: "योजना का नाम",
  addChip: (label: string) => `${label} जोड़ें`,
  removeChip: (value: string) => `${value} हटाएँ`,
  planNamePlaceholder: "अपनी योजना को नाम दें",
  titleAutoHint: "हमने एक नाम चुना — चाहें तो बदलें।",
  whichBooks: "कौन सी किताबें?",
  bookStepCasualHint: "कोई किताब बताएँ, या मैं आपकी पसंद की शैली से सुझाव दूँ।",
  bookStepDefaultHint: "कोई विशेष किताब चुनें, या लक्ष्य के लिए सर्वश्रेष्ठ सामग्री सुझाएँ।",
  bookTitlePlaceholder: "उदा. Laxmikanth — Indian Polity",
  paceBlurb: "रोडमैप आपके समय के अनुसार — पक्का न हो तो खाली छोड़ें।",
  daysToGoal: "लक्ष्य तक दिन",
  minutesPerDay: "रोज़ कितने मिनट पढ़ सकते हैं",
  daysPlaceholder: "उदा. 180",
  minutesPlaceholder: "उदा. 120",
  reviewGoal: "लक्ष्य",
  reviewExam: "परीक्षा",
  reviewFocus: "फोकस",
  reviewRoadmap: "रोडमैप",
  reviewBooks: "किताबें",
  reviewBook: "किताब",
  reviewTimeline: "समयरेखा",
  reviewDaily: "रोज़",
  reviewDays: (n: string) => `${n} दिन`,
  reviewMinPerDay: (n: string) => `${n} मिनट/दिन`,
  reviewReadyHint: "बढ़िया लग रहा है — तैयार होने पर आगे बढ़ें।",
};

export function intakeWizardStrings(locale: PortalLocale): IntakeTable {
  return locale === "hi" ? INTAKE_HI : INTAKE_EN;
}

export function localizedPurposeCopy(
  locale: PortalLocale,
  purpose: LearningPurpose,
): { label: string; description: string } {
  const s = intakeWizardStrings(locale);
  switch (purpose) {
    case "exam_prep":
      return { label: s.purposeExam, description: s.purposeExamDesc };
    case "concept_mastery":
      return { label: s.purposeSubject, description: s.purposeSubjectDesc };
    case "skill_building":
      return { label: s.purposeSkill, description: s.purposeSkillDesc };
    case "school_support":
      return { label: s.purposeSchool, description: s.purposeSchoolDesc };
    case "casual_reading":
      return { label: s.purposeCasual, description: s.purposeCasualDesc };
    default:
      return { label: s.purposeOther, description: s.purposeOtherDesc };
  }
}

// Localized helpers that mirror lib/learning/copy exports but respect locale.
import * as copy from "../learning/copy";

export function localizedRoadmapLabel(locale: PortalLocale): Record<copy.RoadmapSource, string> {
  if (locale === "hi") {
    return {
      ai_generated: "मुझे एक रोडमैप बनाएं",
      self_chosen: "मेरी अपनी योजना है",
    };
  }
  return copy.ROADMAP_LABEL;
}

export function localizedBookLabel(locale: PortalLocale): Record<copy.BookSource, string> {
  if (locale === "hi") {
    return {
      ai_pick: "सर्वश्रेष्ठ पुस्तकें सुझाएँ",
      self_specified: "मैं अपनी पुस्तक चुनूँगा",
    };
  }
  return copy.BOOK_LABEL;
}

/**
 * The auto-suggested plan title is persisted as data the learner can edit, so
 * it has to be generated in their language at creation time — a stored English
 * title cannot be translated after the fact without overwriting their edit.
 */
export function localizedDefaultTitle(
  locale: PortalLocale,
  purpose: copy.LearningPurpose,
  opts: { examName?: string | null; subjects?: string[] } = {},
): string {
  if (locale !== "hi") return copy.defaultTitleFor(purpose, opts);

  const exam = opts.examName?.trim();
  if (purpose === "exam_prep" && exam) return `${exam} की तैयारी`;
  const firstSubject = opts.subjects?.find((s) => s.trim().length > 0)?.trim();
  if (firstSubject) {
    if (purpose === "casual_reading") return `पढ़ाई: ${firstSubject}`;
    if (purpose === "skill_building") return `${firstSubject} सीखना`;
    return firstSubject;
  }
  return localizedPurposeCopy(locale, purpose).label;
}

export function localizedSubjectsCopy(locale: PortalLocale, purpose: copy.LearningPurpose) {
  if (locale === "hi") {
    // Hindi localized placeholders
    if (purpose === "casual_reading")
      return { label: "आप किन शैलियों को पसंद करते हैं", placeholder: "उदा. Sci-fi, Biography" };
    if (purpose === "skill_building")
      return { label: "कौशल या क्षेत्र", placeholder: "उदा. Python, सार्वजनिक बोलना" };
    return { label: "विषय", placeholder: "उदा. Polity, Economics" };
  }
  return copy.subjectsCopy(purpose);
}
